import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    LinearProgress,
    Tab,
    Tabs,
    Theme,
    Typography
} from "@mui/material";
import {
    maskInfo,
    toINR
} from "../../utils/utilityFunctions";
import {
    useEffect,
    useState
} from "react";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { useHistory } from "react-router";
import { GetOrderPreviewResponseBody, OrderPreviewOpenFlowRes } from "../../redux-store/types/cart";
import { GetInvestmentUserDetails } from "../../redux-store/types/api-types";
import { MandateCreation } from "../../redux-store/types/investorProfile";
import { RootStateType } from "../../redux-store/reducers";
import { getOrderPreview, orderPreviewOpenFlow } from "../../redux-store/actions/cart";
import { getInvestmentUserDetails } from "../../redux-store/actions/funds";
import { getMandateCreationDetails } from "../../redux-store/actions/investorProfile";
import FundsDialog from "./FundsDialog";
import NetBanking from "./NetBanking";
import UpiOneTimeMandate from "./UpiOneTimeMandate";
import Neft from "./Neft";
import { SuccessIcon } from "../../assets/SvgIcons";
import { applicantStatusMasters } from "../../utils/constant";
import { getAbbreviatedName } from "../../components/commonComponents";


const tabsArray = ["Net Banking", "UPI/One Time Mandate", "NEFT"]

type Tab = "Net Banking" | "UPI/One Time Mandate" | "NEFT"

interface RouterStateData {
    token: string
    cartId: string
    clientCode?: string
    portfolioId?: string
}

export default function PaymentScreen({
    heading = "Make Payment",
    customProp = "",
}: {
    heading?: string,
    customProp?: string;
}): JSX.Element {
    const dispatch = useDispatch()
    const history = useHistory();

    const routerStateObj = history.location.state as RouterStateData

    const [activeTab, setActiveTab] = useState<Tab>("Net Banking")
    const [isLoading, setLoading] = useState<boolean>(false)
    const [orderPreviewData, setOrderPreviewData] = useState<GetOrderPreviewResponseBody>({} as GetOrderPreviewResponseBody)
    const [investorDetails, setInvestorDetails] = useState<GetInvestmentUserDetails>({} as GetInvestmentUserDetails)
    const [bankDetails, setBankDetails] = useState<MandateCreation[]>([])
    const [isFundsDialogVisible, setFundsDialogVisible] = useState<boolean>(false)
    const [isOrderFailedDialogVisible, setOrderFailedDialogVisible] = useState<boolean>(false)
    const [isOrderPlacedSuccessDialogVisible, setOrderPlacedSuccessDialogVisible] = useState<boolean>(false)

    const { investorClientId = "" } = useSelector((store: RootStateType) => store.id);

    const { selectedPortfolio } = useSelector((state: RootStateType) => state.selectedPortfiolio)
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const isFundPaymentScreen = (history?.location?.pathname ?? "").includes('fund-payment')

    const fetchBankDetails = async () => {
        const response = await (dispatch(getMandateCreationDetails({ clientCode: routerStateObj?.clientCode ? (routerStateObj?.clientCode) : investorClientId, portfolioId: routerStateObj?.portfolioId ? (routerStateObj?.portfolioId) : selectedPortfolio?.value })) as unknown as any)

        setBankDetails(response ?? []);
    }

    const fetchOrderPreview = async () => {
        if (isFundPaymentScreen) {
            const response = await (dispatch(orderPreviewOpenFlow(routerStateObj?.token ?? "")) as unknown as Promise<OrderPreviewOpenFlowRes>)

            if (response?.isSuccess) {
                setOrderPreviewData(response)
                setInvestorDetails({
                    name: response?.name ?? "",
                    email: response?.email ?? "",
                    mobile: response?.mobile ?? "",
                    status: response?.status ?? "",
                    accountType: response?.accountType ?? ""
                })

                if (response?.paymentRequired) {
                    if (response?.orders?.some(obj => !obj.bseOrderStatus)) {
                        setOrderFailedDialogVisible(true)
                    }

                    // await fetchBankDetails()
                } else {
                    if (response?.orders?.some(obj => !obj.bseOrderStatus)) {
                        setOrderFailedDialogVisible(true)
                        return
                    }

                    setOrderPlacedSuccessDialogVisible(true)
                }
            }
        }
        else {
            const response = await (dispatch(getOrderPreview(routerStateObj?.token ?? "")) as unknown as Promise<GetOrderPreviewResponseBody>)

            if (response?.isSuccess) {
                setOrderPreviewData(response)

                if (response?.paymentRequired) {
                    if (response?.orders?.some(obj => !obj.bseOrderStatus)) {
                        setOrderFailedDialogVisible(true)
                    }

                    await fetchBankDetails()
                } else {
                    if (response?.orders?.some(obj => !obj.bseOrderStatus)) {
                        setOrderFailedDialogVisible(true)
                        return
                    }

                    setOrderPlacedSuccessDialogVisible(true)
                }
            }
        }
    }

    const fetchInvestorDetails = async () => {
        const response = await (dispatch(getInvestmentUserDetails(
            { clientCode: routerStateObj?.clientCode ? (routerStateObj?.clientCode) : investorClientId, portfolioId: routerStateObj?.portfolioId ? (routerStateObj?.portfolioId) : selectedPortfolio?.value }
        )) as unknown as Promise<GetInvestmentUserDetails>)

        setInvestorDetails(response)
    }

    const fetchData = async () => {
        try {
            setLoading(true)
            if (isFundPaymentScreen) {
                await fetchOrderPreview()
            } else {
                await Promise.allSettled([fetchOrderPreview(), fetchInvestorDetails()])
            }
        } catch (error) {
            console.log("Error:", error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    const handleTabChange = (event: React.SyntheticEvent, selectedTabIndex: number) => {
        setActiveTab(tabsArray[selectedTabIndex] as Tab);
    };

    const handleOpenFundsDialog = () => {
        setFundsDialogVisible(true)
    }

    const handleCloseFundsDialog = () => {
        setFundsDialogVisible(false)
    }

    return (
        <>
            <Box sx={{ ...(customProp === 'spacing' ? { padding: '20px 45px' } : {}) }}>
                <Typography
                    variant="h1"
                    sx={(theme: Theme) => ({
                        lineHeight: "32.5px",
                        color: theme.palette.text.primary
                    })}
                >
                    {heading}
                </Typography>
                {isLoading
                    ?
                    <LinearProgress sx={{ mt: '25px' }} />
                    :
                    (
                        <Box sx={{ marginTop: "25px" }}>
                            <Grid
                                container
                                spacing={3}
                            >
                                {orderPreviewData?.paymentRequired && (
                                    <Grid
                                        item
                                        xs={12}
                                        lg={8}
                                        order={{ xs: 2, lg: 1 }}
                                    >
                                        <Box
                                            sx={(theme: Theme) => ({
                                                borderRadius: "20px",
                                                padding: "25px 30px 30px 30px",
                                                backgroundColor: theme.palette.common.white,
                                                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                                            })}
                                        >
                                            <Typography
                                                variant="h2"
                                                sx={(theme: Theme) => ({
                                                    color: theme.palette.text.primary,
                                                    lineHeight: "25px",
                                                    letterSpacing: "0.01px"
                                                })}
                                            >
                                                Choose Payment Mode
                                            </Typography>
                                            <Box sx={{ marginTop: "20px" }}>
                                                <Tabs
                                                    value={tabsArray.indexOf(activeTab)}
                                                    onChange={handleTabChange}
                                                >
                                                    {tabsArray.map((tab: string) => (
                                                        <Tab
                                                            key={tab}
                                                            label={tab}
                                                        />
                                                    ))}
                                                </Tabs>
                                                <Box sx={{ marginTop: "20px" }}>
                                                    {activeTab === "Net Banking" && (
                                                        <NetBanking
                                                            bankDetailsData={bankDetails ?? []}
                                                            orderPreviewData={orderPreviewData}
                                                        />
                                                    )}
                                                    {activeTab === "UPI/One Time Mandate" && (
                                                        <UpiOneTimeMandate
                                                            bankDetailsData={bankDetails ?? []}
                                                            orderPreviewData={orderPreviewData}
                                                        />
                                                    )}
                                                    {activeTab === "NEFT" && (
                                                        <Neft
                                                            bankDetailsData={bankDetails ?? []}
                                                            orderPreviewData={orderPreviewData}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                )}
                                <Grid
                                    item
                                    xs={12}
                                    lg={!orderPreviewData?.paymentRequired ? 12 : 4}
                                    order={{ xs: 1, lg: 2 }}
                                >
                                    <Box
                                        sx={(theme: Theme) => ({
                                            borderRadius: "20px",
                                            padding: "25px 30px 30px 30px",
                                            backgroundColor: theme.palette.common.white,
                                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                                        })}
                                    >
                                        <Box
                                            sx={(theme: Theme) => ({
                                                display: "flex",
                                                columnGap: "15px",
                                                alignItems: "center",
                                                paddingBottom: "35px",
                                                borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`
                                            })}>
                                            {/* <Avatar
                                                sx={(theme: Theme) => ({
                                                    ...theme.typography.h3,
                                                    fontSize: "24px",
                                                    width: "58px",
                                                    height: "58px",
                                                    lineHeight: "30px",
                                                    letterSpacing: "0.02px",
                                                    color: theme.palette.primary[500],
                                                    backgroundColor: theme.palette.primary[50]
                                                })}
                                            >
                                                {getAbbreviatedName(investorDetails?.name ?? "")}
                                            </Avatar> */}
                                             {getAbbreviatedName(investorDetails?.name ?? "")}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    rowGap: "5px",
                                                }}
                                            >
                                                <Typography
                                                    variant="h2"
                                                    sx={(theme: Theme) => ({
                                                        lineHeight: "25px",
                                                        letterSpacing: "0.01px",
                                                        color: theme.palette.text.primary,
                                                        marginBottom: "5px"
                                                    })}
                                                >
                                                    {maskInfo(investorDetails?.name ?? "")}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        columnGap: "5px"
                                                    }}
                                                >
                                                    <Typography
                                                        variant='body_500'
                                                        sx={(theme: Theme) => ({
                                                            lineHeight: "20px",
                                                            letterSpacing: "0.02px",
                                                            color: theme.palette.text.primary,
                                                            whiteSpace: "nowrap"
                                                        })}
                                                    >
                                                        Client Code:
                                                    </Typography>
                                                    <Typography
                                                        variant='body_500'
                                                        sx={(theme: Theme) => ({
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            lineHeight: "20px",
                                                            letterSpacing: "0.02px",
                                                            color: theme.palette.text.secondary,
                                                            wordBreak: "break-word",
                                                            maxWidth: "100%",
                                                        })}
                                                    >
                                                        {routerStateObj?.clientCode ? (routerStateObj?.clientCode) : investorClientId}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "5px",
                                                        flexWrap: "wrap"
                                                    }}
                                                >
                                                    {/* <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            lineHeight: "20px",
                                                            letterSpacing: "0.02px",
                                                            color: theme.palette.text.secondary,
                                                            textTransform: "capitalize"
                                                        })}
                                                    >
                                                        {investorDetails?.applicationType ?? ""}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            width: "3px",
                                                            height: "3px",
                                                            backgroundColor: "text.secondary",
                                                            alignSelf: "center",
                                                            borderRadius: "50%"
                                                        }}
                                                    /> */}
                                                    <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            lineHeight: "20px",
                                                            letterSpacing: "0.02px",
                                                            color: theme.palette.text.secondary
                                                        })}
                                                    >
                                                        {orderPreviewData?.portfolioId ?? ""}
                                                    </Typography>
                                                    {selectedPortfolio?.label && <Box
                                                        sx={{
                                                            width: "3px",
                                                            height: "3px",
                                                            backgroundColor: "text.secondary",
                                                            alignSelf: "center",
                                                            borderRadius: "50%"
                                                        }}
                                                    />}
                                                    {selectedPortfolio?.label && <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            lineHeight: "20px",
                                                            letterSpacing: "0.02px",
                                                            color: theme.palette.text.secondary
                                                        })}
                                                    >
                                                        {selectedPortfolio?.label ?? ""}
                                                    </Typography>}
                                                    <Box
                                                        sx={{
                                                            width: "3px",
                                                            height: "3px",
                                                            backgroundColor: "text.secondary",
                                                            alignSelf: "center",
                                                            borderRadius: "50%"
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            lineHeight: "20px",
                                                            letterSpacing: "0.02px",
                                                            color: theme.palette.text.secondary
                                                        })}
                                                    >
                                                        {investorDetails?.accountType ?? ""}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            width: "3px",
                                                            height: "3px",
                                                            backgroundColor: "text.secondary",
                                                            alignSelf: "center",
                                                            borderRadius: "50%"
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            lineHeight: "20px",
                                                            letterSpacing: "0.02px",
                                                            color: theme.palette.text.secondary,
                                                            textTransform: "capitalize"
                                                        })}
                                                    >
                                                        {investorDetails?.modeOfHolding ?? ""}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            width: "3px",
                                                            height: "3px",
                                                            backgroundColor: "text.secondary",
                                                            alignSelf: "center",
                                                            borderRadius: "50%"
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            lineHeight: "20px",
                                                            letterSpacing: "0.02px",
                                                            color: theme.palette.text.secondary
                                                        })}
                                                    >
                                                        {/* {
                                                            investorDetails?.status ?
                                                                applicantStatusMasters[investorDetails.status] ?
                                                                    applicantStatusMasters[investorDetails.status] : investorDetails.status
                                                                : "N/A"
                                                        } */}
                                                        {investorDetails?.investorType ?? ""}
                                                    </Typography>
                                                    {(`${investorDetails?.isFamilyHead ?? ""}`.toLowerCase() === "y") && (
                                                        <>
                                                            <Box
                                                                sx={{
                                                                    width: "3px",
                                                                    height: "3px",
                                                                    backgroundColor: "text.secondary",
                                                                    alignSelf: "center",
                                                                    borderRadius: "50%"
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body_500"
                                                                sx={(theme: Theme) => ({
                                                                    lineHeight: "20px",
                                                                    letterSpacing: "0.02px",
                                                                    color: theme.palette.text.secondary
                                                                })}
                                                            >
                                                                Head Of Family
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                marginTop: "35px",
                                                display: "flex",
                                                flexDirection: "column",
                                                rowGap: "15px"
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    columnGap: "10px",
                                                    alignItems: "center"
                                                }}
                                            >
                                                <Typography
                                                    variant="body_500"
                                                    sx={(theme: Theme) => ({
                                                        lineHeight: "14px",
                                                        color: theme.palette.text.primary
                                                    })}
                                                >
                                                    Paying For:
                                                </Typography>
                                                <Button
                                                    variant="text"
                                                    sx={(theme: Theme) => ({
                                                        cursor: "pointer",
                                                        color: theme.palette.primary[500],
                                                        padding: "0px 5px",
                                                        ...theme.typography.body_500
                                                    })}
                                                    onClick={handleOpenFundsDialog}
                                                >
                                                    Show Funds
                                                </Button>
                                            </Box>
                                            {(Number(orderPreviewData?.payable_amount ?? 0) !== 0) && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        columnGap: "10px",
                                                        alignItems: "center"
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            lineHeight: "14px",
                                                            color: theme.palette.text.primary
                                                        })}
                                                    >
                                                        Payable Amount:
                                                    </Typography>
                                                    <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            fontWeight: 600,
                                                            lineHeight: "25px",
                                                            color: theme.palette.text.primary
                                                        })}
                                                    >
                                                        {toINR({ value: (orderPreviewData?.payable_amount ?? ""), locale, currencyCode })}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    )
                }
            </Box>
            <FundsDialog
                onClose={handleCloseFundsDialog}
                open={isFundsDialogVisible}
                data={orderPreviewData}
            />
            {/* Order Failed Dialog Start */}
            <Dialog
                aria-labelledby="order-failed-dialog"
                open={isOrderFailedDialogVisible}
                PaperProps={{
                    sx: (theme: Theme) => ({
                        maxWidth: '550px',
                        width: "100%",
                        borderRadius: "12px",
                        padding: "24px",
                        backgroundColor: theme.palette.common.white
                    }),
                }}
            >
                <DialogContent>
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            lineHeight: "30px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.primary,
                            textAlign: "center",
                            paddingLeft: "10px",
                            paddingRight: "10px"
                        })}
                    >
                        One or more orders have failed. Please click on Show Funds for more details.
                    </Typography>
                </DialogContent>
                <DialogActions
                >
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => setOrderFailedDialogVisible(false)}
                        sx={(theme: Theme) => ({
                            ...theme.typography.body_500,
                            fontWeight: 600,
                            lineHeight: "20px",
                            color: theme.palette.common.white,
                            borderRadius: "4px",
                            padding: "13px 20px",
                            backgroundColor: theme.palette.primary[500]
                        })}
                    >
                        Okay
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Order Failed Dialog End */}
            {/* In case of Payment not required. Order Placed Successfully Start */}
            <Dialog
                aria-labelledby="success-dialog"
                open={isOrderPlacedSuccessDialogVisible}
                PaperProps={{
                    sx: (theme: Theme) => ({
                        maxWidth: '550px',
                        width: "100%",
                        borderRadius: "12px",
                        padding: "24px",
                        backgroundColor: theme.palette.common.white
                    }),
                }}
            >
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        rowGap: "12px",
                        padding: "15px",
                        overflow: "hidden"
                    }}
                >
                    <SuccessIcon />
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: "25px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary
                        })}
                    >
                        Success
                    </Typography>
                    <Typography
                        variant="subtitle_400"
                        sx={(theme: Theme) => ({
                            lineHeight: "25px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.secondary,
                            textAlign: "center",
                            paddingLeft: "10px",
                            paddingRight: "10px"
                        })}
                    >
                        Order placed successfully. Please Go to Dashboard for more details.
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: "0px",
                        marginTop: "40px",
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => {
                            history.replace('/investor-dashboard')
                            setOrderPlacedSuccessDialogVisible(false)
                        }}
                        sx={(theme: Theme) => ({
                            ...theme.typography.body_500,
                            fontWeight: 600,
                            lineHeight: "20px",
                            color: theme.palette.common.white,
                            borderRadius: "4px",
                            padding: "13px 20px",
                            backgroundColor: theme.palette.primary[500]
                        })}
                    >
                        Go to Dashboard
                    </Button>
                </DialogActions>
            </Dialog>
            {/* In case of Payment not required. Order Placed Successfully End */}
        </>
    )
}
